'use client';

import { IProductDetail } from '@/api/types/public';
import Image from '@/components/Image';
import Popover from '@/components/Popover';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useStore } from '@/providers/app-store-provider';
import { IS_DE_FR } from '@/utils/constants';
import { EFrameLeg, EPrescriptionType } from '@/utils/enums/goods';
import { EPlatform } from '@/utils/enums/platform-enum';
import { formatNumber, setSession } from '@/utils/tools';
import { Box, Typography } from '@mui/material';
import { QuestionFilled, QuestionOutlined } from '@zeelool/icon';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useFrameSizeAfterFormat } from '../hooks/useFrameSizeAfterFormat';
import {
  BridgeFitContent,
  PdRangeContent,
  ProgressiveContent,
  renderSizeContent,
  RxRangeContent,
  TITLES,
} from './components';
import { ParamItem } from './modules/ParamItem';

export interface ProductParamsProps {
  detail: IProductDetail | any;
  SDProductDetailsPageBtnClick?: (label?: string) => void;
  variant?: 'default' | 'tab'; // 新增样式变体参数
}

export default function ProductParams({
  detail,
  SDProductDetailsPageBtnClick,
  variant = 'default',
}: ProductParamsProps) {
  const t = useTranslations('goodsDetail');
  const locales = useLocale();
  const inches = useStore((state) => state.inches);
  const setInches = useStore((state) => state.setInches);
  const router = useRouter();
  const PROGRESSIVE_CONTENT = <ProgressiveContent />;
  const RX_RANGE_CONTENT = <RxRangeContent />;
  const PD_RANGE_CONTENT = <PdRangeContent />;

  const [popTitle, setPopTitle] = useState<string>('');
  const { deviceType } = useContext(ResponsiveContext);
  // const isWeb = deviceType === EPlatform.WEB;
  const [isShowPopover, setIsShowPopover] = useState<boolean>(false);
  const [popContent, setPopContent] = useState<React.ReactNode>('');
  const { totalLengthValue, glassWidthValue, lensHeightValue, legLengthValue, bridgeValue } =
    useFrameSizeAfterFormat(detail, inches);

  useEffect(() => {
    if (popTitle === TITLES.SIZE) {
      setPopContent(renderSizeContent(detail));
    }
  }, [detail, popTitle]);

  // 是否展示rx范围参数
  const showRxRange = () => {
    // product_lens是否支持配镜片 1支持 2不支持
    const { prescription_type = [], rx = '', product_lens } = detail;
    if (product_lens === 2) {
      // 不支持配镜时，不展示此选项
      return false;
    }
    if (
      prescription_type.length === 1 && // 仅勾选
      (prescription_type[0] === EPrescriptionType.NON_PRESCRIPTION ||
        prescription_type[0] === EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION)
    ) {
      // 仅勾选Non-Prescription，或，Sunglasses Non-Prescription时，不显示此项。
      return false;
    }
    if (
      prescription_type.length === 2 && // 勾选了两个 并且勾选的这两个是 Non-Prescription 和 Sunglasses Non-Prescription也不展示
      prescription_type.includes(EPrescriptionType.NON_PRESCRIPTION) &&
      prescription_type.includes(EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION)
    ) {
      return false;
    }
    if (!rx) {
      // 如果rx没有值，不显示
      return false;
    }
    return true;
  };

  const shapePath = useMemo(() => {
    const { product_frame_shape, nav_shape_group_id } = detail;

    if (nav_shape_group_id) {
      return {
        id: nav_shape_group_id,
        name: product_frame_shape,
      };
    }
    return null;
  }, [detail]);

  const materialPath = useMemo(() => {
    const { product_frame_material, nav_material_group_id } = detail;
    if (nav_material_group_id) {
      return {
        id: nav_material_group_id,
        name: product_frame_material,
      };
    }
    return null;
  }, [detail]);

  // 是否展示pd范围参数
  const showPdRange = () => {
    const { prescription_type = [], pd_min = '', pd_max = '', product_lens } = detail;
    if (product_lens === 2) {
      // 不支持配镜时，不展示此选项
      return false;
    }
    if (
      prescription_type.length === 1 && // 仅勾选
      (prescription_type[0] === EPrescriptionType.NON_PRESCRIPTION ||
        prescription_type[0] === EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION)
    ) {
      // 仅勾选Non-Prescription，或，Sunglasses Non-Prescription时，不显示此项。
      return false;
    }
    if (
      prescription_type.length === 2 && // 勾选了两个 并且勾选的这两个是 Non-Prescription 和 Sunglasses Non-Prescription也不展示
      prescription_type.includes(EPrescriptionType.NON_PRESCRIPTION) &&
      prescription_type.includes(EPrescriptionType.SUNGLASSES_NON_PRESCRIPTION)
    ) {
      return false;
    }
    if (!pd_min || !pd_max) {
      // 这两个值有一个为空就不展示
      return false;
    }
    return true;
  };

  // 是否展示progressive参数
  const showProgressive = () => {
    // 网站商品分类为"镜框/太阳镜"时，此项不显示
    // is_sunglasses 是否"镜框/太阳镜"分类 1是 2不是
    // 不支持配镜时，不展示此选项
    const { is_sunglasses = 1, product_lens } = detail;
    if (product_lens === 2) {
      return false;
    }
    if (is_sunglasses === 1) {
      return false;
    }
    return true;
  };

  // 是否支持渐进镜
  const isSupportProgressive = () => {
    const { prescription_type = [] } = detail; // 处方类型中，是否包含的有渐进镜
    return prescription_type.includes(EPrescriptionType.PROGRESSIVE);
  };

  const lang = useMemo(() => {
    if (locales === 'de') {
      return 'de-DE';
    }
    if (locales === 'fr') {
      return 'fr-FR';
    }
    return 'en-US';
  }, [locales]);

  const weightValue = useMemo(() => {
    if (detail.weight) {
      return `${formatNumber(detail.weight, 1, lang)} ${t('params.values.gram')}`;
    }
    return '-';
  }, [detail.weight]);

  const getBridgeFitTranslation = (value: string): string => {
    return t(`params.values.${value}`);
  };

  // 根据变体返回不同的容器样式
  const getContainerClassName = () => {
    if (variant === 'tab') {
      return 'bg-[#f4f4f4] py-[3.75vw] min-w-[700px] rounded-[4px] mt-[1.67vw] mx-auto';
    }
    return `w-full px-[16px] rounded-[4px] pb-[46px] ${deviceType === EPlatform.WEB ? 'bg-[#f7f7f7] pt-[16px]' : 'bg-auxiliary-gray pt-[20px]'}`;
  };

  // 根据变体返回不同的布局样式
  const getLayoutStyle = () => {
    if (variant === 'tab') {
      return {
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'flex-start' as const,
        justifyContent: 'space-between' as const,
        gap: '55px',
        marginRight: IS_DE_FR ? '112px' : '172px',
      };
    }
    return {
      display: 'flex',
      flexDirection: deviceType === EPlatform.WEB ? 'column' : 'row',
      alignItems: deviceType === EPlatform.WEB ? 'flex-start' : 'center',
    };
  };

  // 根据变体返回参数列表样式
  const getParamsListStyle = (isSecondColumn = false) => {
    if (variant === 'tab') {
      return {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '16px',
      };
    }

    // 计算 margin-right：只有非WEB端的第一列需要右边距
    const marginRight = deviceType !== EPlatform.WEB && !isSecondColumn ? 7 : 0;

    // 计算 margin-top：只有WEB端的第二列需要上边距
    const marginTop = deviceType === EPlatform.WEB && isSecondColumn ? 5 : 0;

    return {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '16px',
      mr: marginRight,
      mt: marginTop,
    };
  };
  return (
    <Box className={getContainerClassName()}>
      <section className="product-params mx-auto">
        <Box
          className="detail"
          sx={{
            margin: '0 auto',
            width: '100%',
          }}
        >
          <Box className={`detail-container ${variant === 'tab' ? 'flex mx-auto w-fit' : ''}`}>
            <Box sx={getLayoutStyle()}>
              {/* 第一列参数 */}
              <Box className="detail-right-params" sx={getParamsListStyle(false)}>
                <ParamItem label={t('params.labels.sku')} value={detail.shop_sku} />

                <ParamItem
                  label={t('params.labels.shape')}
                  value={
                    shapePath?.id ? (
                      <Link
                        href={`/goods-list/${shapePath?.id}`}
                        aria-label={t('params.ariaLabels.shape', { name: shapePath?.name })}
                        className="hover:underline hover:text-primary-purple font-bb"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setSession('zl_goodsListEnterPageScene', '商品详情页形状参数');
                          router.push(`/goods-list/${shapePath?.id}`);
                        }}
                      >
                        {shapePath?.name}
                      </Link>
                    ) : (
                      detail.product_frame_shape || '-'
                    )
                  }
                />

                {detail.product_size && (
                  <ParamItem
                    label={t('params.labels.size')}
                    value={detail.product_size || '-'}
                    infoPopover={
                      <WapOnly>
                        <QuestionOutlined
                          className="text-[#B0B0B0] text-[14px]"
                          onClick={() => {
                            setIsShowPopover(true);
                            setPopTitle(t('titles.size'));
                            setPopContent(renderSizeContent(detail));
                          }}
                        />
                      </WapOnly>
                    }
                  />
                )}

                {detail.product_frame_material && (
                  <ParamItem
                    label={t('params.labels.material')}
                    value={
                      materialPath?.id ? (
                        <Link
                          href={`/goods-list/${materialPath?.id}`}
                          aria-label={t('params.ariaLabels.material', {
                            name: materialPath?.name,
                          })}
                          className="hover:underline hover:text-primary-purple font-bb"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setSession('zl_goodsListEnterPageScene', '商品详情页材质参数');
                            router.push(`/goods-list/${materialPath?.id}`);
                          }}
                        >
                          {materialPath?.name}
                        </Link>
                      ) : (
                        detail.product_frame_material || '-'
                      )
                    }
                  />
                )}

                <ParamItem
                  label={t('params.labels.rim')}
                  value={detail.product_frame_type || '-'}
                />

                {showProgressive() && (
                  <ParamItem
                    label={t('params.labels.progressive')}
                    value={isSupportProgressive() ? t('params.values.yes') : t('params.values.no')}
                    infoPopover={
                      <>
                        <WebOnly>
                          <Popover
                            id="progressive-popover"
                            content={
                              <>
                                <p className="text-[14px] font-bb mb-12">
                                  {t('params.labels.progressive')}
                                </p>
                                <ProgressiveContent paragraphClassName="text-[14px] leading-[22px] font-ir" />
                              </>
                            }
                            triggerComponent={
                              <QuestionFilled className="text-[#A3A3A3] text-[16px] ml-2 cursor-pointer" />
                            }
                            onOpen={() => {
                              SDProductDetailsPageBtnClick?.(t('params.labels.progressive'));
                            }}
                            // 不锁住滚动，因为用户交互还会继续往下滑，这里使用popover的组件是因为这个会在弹窗中使用
                            disableScrollLock
                          />
                        </WebOnly>
                        <WapOnly>
                          <QuestionOutlined
                            className="text-[#B0B0B0] text-[14px]"
                            onClick={() => {
                              setIsShowPopover(true);
                              setPopTitle(t('titles.progressive'));
                              setPopContent(PROGRESSIVE_CONTENT);
                            }}
                          />
                        </WapOnly>
                      </>
                    }
                  />
                )}

                <ParamItem
                  label={t('params.labels.springHinge')}
                  value={
                    [EFrameLeg.SPRING_LEG, EFrameLeg.SLINGSHOT].includes(detail.frame_leg)
                      ? t('params.values.yes')
                      : t('params.values.no')
                  }
                />

                <ParamItem
                  label={t('params.labels.adjustableNosepads')}
                  value={detail.frame_nose || '-'}
                />
              </Box>

              {/* 第二列参数 */}
              <Box className="detail-right-params" sx={getParamsListStyle(true)}>
                <ParamItem label={t('params.labels.weight')} value={weightValue} />

                <ParamItem label={t('params.labels.frameWidth')} value={totalLengthValue} />

                <ParamItem
                  label={t('params.labels.bridgeFit')}
                  value={
                    detail.frame_bridge_value
                      ? getBridgeFitTranslation(detail.frame_bridge_value)
                      : '-'
                  }
                  infoPopover={
                    <>
                      <WebOnly>
                        <Popover
                          id="bridge-fit-popover"
                          content={
                            <>
                              <p className="text-[14px] font-bb mb-12">
                                {getBridgeFitTranslation(detail.frame_bridge_value)}
                              </p>
                              <BridgeFitContent
                                detail={detail}
                                paragraphClassName="text-[14px] leading-[22px] font-ir"
                              />
                            </>
                          }
                          triggerComponent={
                            <QuestionFilled className="text-[#A3A3A3] text-[16px] ml-2 cursor-pointer" />
                          }
                          onOpen={() => {
                            SDProductDetailsPageBtnClick?.(t('params.labels.bridgeFit'));
                          }}
                          // 不锁住滚动，因为用户交互还会继续往下滑，这里使用popover的组件是因为这个会在弹窗中使用
                          disableScrollLock
                        />
                      </WebOnly>
                      <WapOnly>
                        <QuestionOutlined
                          className="text-[#B0B0B0] text-[14px]"
                          onClick={() => {
                            setIsShowPopover(true);
                            setPopTitle(t('params.labels.bridgeFit'));
                            setPopContent(<BridgeFitContent detail={detail} />);
                          }}
                        />
                      </WapOnly>
                    </>
                  }
                />

                <ParamItem label={t('params.labels.lensWidth')} value={glassWidthValue} />

                <ParamItem label={t('params.labels.lensHeight')} value={lensHeightValue} />

                <ParamItem label={t('params.labels.templeLength')} value={legLengthValue} />

                {showRxRange() && (
                  <ParamItem
                    label={t('params.labels.rxRange')}
                    value={detail.rx}
                    infoPopover={
                      <>
                        <WebOnly>
                          <Popover
                            id="rx-range-popover"
                            content={
                              <>
                                {/* <p className="text-[20px] leading-[22px] font-bb mb-12">
                                  {t('params.labels.rxRange')}
                                </p> */}
                                <RxRangeContent paragraphClassName="text-[14px] leading-[22px] font-ir" />
                              </>
                            }
                            triggerComponent={
                              <QuestionFilled className="text-[#A3A3A3] text-[16px] ml-2 cursor-pointer" />
                            }
                            onOpen={() => {
                              SDProductDetailsPageBtnClick?.(t('params.labels.rxRange'));
                            }}
                            // 不锁住滚动，因为用户交互还会继续往下滑，这里使用popover的组件是因为这个会在弹窗中使用
                            disableScrollLock
                          />
                        </WebOnly>
                        <WapOnly>
                          <QuestionOutlined
                            className="text-[#B0B0B0] text-[14px]"
                            onClick={() => {
                              setIsShowPopover(true);
                              setPopTitle(t('titles.rxRange'));
                              setPopContent(RX_RANGE_CONTENT);
                            }}
                          />
                        </WapOnly>
                      </>
                    }
                  />
                )}

                {showPdRange() && (
                  <ParamItem
                    label={t('params.labels.pdRange')}
                    value={`${detail.pd_min}~${detail.pd_max}`}
                    infoPopover={
                      <>
                        <WebOnly>
                          <Popover
                            id="pd-range-popover"
                            content={
                              <>
                                <p className="text-[14px] font-bb mb-12">
                                  {t('params.labels.pdRange')}
                                </p>
                                <PdRangeContent paragraphClassName="text-[14px] leading-[22px] font-ir" />
                              </>
                            }
                            triggerComponent={
                              <QuestionFilled className="text-[#A3A3A3] text-[16px] ml-2 cursor-pointer" />
                            }
                            onOpen={() => {
                              SDProductDetailsPageBtnClick?.(t('params.labels.pdRange'));
                            }}
                            // 不锁住滚动，因为用户交互还会继续往下滑，这里使用popover的组件是因为这个会在弹窗中使用
                            disableScrollLock
                          />
                        </WebOnly>
                        <WapOnly>
                          <QuestionOutlined
                            className="text-[#B0B0B0] text-[14px]"
                            onClick={() => {
                              setIsShowPopover(true);
                              setPopTitle(t('titles.pdRange'));
                              setPopContent(PD_RANGE_CONTENT);
                            }}
                          />
                        </WapOnly>
                      </>
                    }
                  />
                )}
              </Box>
            </Box>

            {/* 眼镜图 */}
            <Box
              className="detail-left"
              sx={{
                position: 'relative',
                boxSizing: 'border-box',
                mt: variant === 'tab' ? 0 : 6,
              }}
            >
              <Box sx={{ position: 'relative', textAlign: 'center' }}>
                <Image
                  className="glass-front block h-auto w-[322px] 2xl:w-[20.83vw]"
                  src="/goods-detail/glass_front_1.png"
                  alt={t('params.images.frontView')}
                  width={322}
                  height={122}
                />
                <Image
                  className="glass-backend block w-[320px] mt-[28px] h-auto 2xl:w-[20.83vw] 2xl:mt-[1.72vw]"
                  src="/goods-detail/glass_backend_1.png"
                  alt={t('params.images.backView')}
                  width={320}
                  height={106}
                />
                <div
                  className={`${
                    deviceType === EPlatform.WEB
                      ? 'right-0'
                      : 'left-[50%] translate-x-[-50%] text-auxiliary-text'
                  } absolute bottom-[-32px] font-normal text-[12px] leading-[14px] text-right underline cursor-pointer`}
                  onClick={() => {
                    SDProductDetailsPageBtnClick?.('show in inches');
                    setInches(!inches);
                  }}
                >
                  {inches ? t('params.values.showInMm') : t('params.values.showInInches')}
                </div>
                <ul>
                  {/* 镜框宽度 */}
                  <li className="flex flex-col items-center text-center absolute text-[12px] top-[-16px] left-[43%]">
                    <Typography
                      component="span"
                      sx={{
                        display: totalLengthValue ? 'block' : 'none',
                        fontSize: '12px',
                      }}
                    >
                      {totalLengthValue}
                    </Typography>
                  </li>
                  {/* 镜片宽度 */}
                  <li className="flex flex-col items-center text-center absolute text-[14px] top-[24%] left-[20%]">
                    <Typography
                      component="span"
                      sx={{
                        display: glassWidthValue !== '-' ? 'block' : 'none',
                        fontSize: '12px',
                      }}
                    >
                      {glassWidthValue}
                    </Typography>
                  </li>
                  {/* bridge宽度 */}
                  <li className="flex flex-col items-center text-center absolute text-[14px] top-[39%] left-[44.2%]">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '12px',
                        display: bridgeValue !== '-' ? 'block' : 'none',
                      }}
                    >
                      {bridgeValue}
                    </Typography>
                  </li>
                  {/* 镜片高度 */}
                  <li className="flex flex-col items-center text-center absolute text-[14px] top-[24%] left-[70%]">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '12px',
                        display: lensHeightValue !== '-' ? 'block' : 'none',
                      }}
                    >
                      {lensHeightValue}
                    </Typography>
                  </li>
                  {/* 镜腿长度 */}
                  <li className="flex flex-col items-center text-center absolute text-[14px] bottom-[-16px] right-[43.5%]">
                    <Typography
                      component="span"
                      sx={{
                        display: legLengthValue !== '-' ? 'block' : 'none',
                        fontSize: '12px',
                      }}
                    >
                      {legLengthValue}
                    </Typography>
                  </li>
                </ul>
              </Box>
            </Box>
          </Box>
        </Box>
      </section>
      <Popup
        open={isShowPopover}
        onClose={() => setIsShowPopover(false)}
        title={popTitle}
        position="bottom"
        height="76vh"
      >
        <Box>{popContent}</Box>
      </Popup>
    </Box>
  );
}
